import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { SessionUser, UserOrganizationMembership } from "@platform/shared";
import { apiFetch, ApiRequestError } from "@/lib/api-client";

/**
 * Real auth context (Step 3).
 *
 * Backed entirely by the API's HttpOnly session cookie -- this context
 * never stores a token itself, only the user/organization data the server
 * returns. `credentials: "include"` in `apiFetch` is what actually sends
 * the cookie; there is nothing here for an XSS payload to steal.
 *
 * This is a UX convenience, not a security boundary: hiding a nav link
 * because `isAuthenticated` is false doesn't stop a direct API call. The
 * real boundary is the backend's `requireAuth`/`requireOrganizationMember`
 * middleware (apps/api/src/middleware/), which runs regardless of what
 * this context believes.
 */

interface AuthContextValue {
  user: SessionUser["user"] | null;
  organizations: UserOrganizationMembership[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (params: {
    email: string;
    password: string;
    fullName: string;
    organizationName: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  organizations: [],
  isAuthenticated: false,
  isLoading: false,
  error: null,
  login: async () => undefined,
  signup: async () => undefined,
  logout: async () => undefined,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<SessionUser>("/api/auth/session");
      setSession(data);
    } catch (err) {
      // 401 just means "not logged in" -- not an error worth surfacing.
      if (!(err instanceof ApiRequestError && err.status === 401)) {
        console.error("Failed to load session", err);
      }
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const data = await apiFetch<SessionUser>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      setSession(data);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Something went wrong. Please try again.");
      throw err;
    }
  }, []);

  const signup = useCallback(
    async (params: { email: string; password: string; fullName: string; organizationName: string }) => {
      setError(null);
      try {
        const data = await apiFetch<SessionUser>("/api/auth/signup", {
          method: "POST",
          body: JSON.stringify(params),
        });
        setSession(data);
      } catch (err) {
        setError(err instanceof ApiRequestError ? err.message : "Something went wrong. Please try again.");
        throw err;
      }
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } finally {
      setSession(null);
    }
  }, []);

  const value: AuthContextValue = {
    user: session?.user ?? null,
    organizations: session?.organizations ?? [],
    isAuthenticated: session !== null,
    isLoading,
    error,
    login,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
